import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { EMPTY_STATE, postVersion, ProblemError, StaleVersionError, type DemoRunResult } from "@/api";
import { Button } from "@/components/Button";
import { StateMessage } from "@/components/StateMessage";

import styles from "./SchemaScreen.module.css";

/** Design 2026-09-23 §3.2, verbatim. */
export const ALREADY_HAS_OUTLINE =
  "Dieser Vorgang hat schon einen Umriss. Lies die Zeichnung in einen neuen Vorgang ein oder ersetze den Umriss.";
export const NOTHING_TO_TAKE_OVER =
  "Diese Lesung hat keinen Umriss, den du übernehmen kannst. Zeichne den Umriss selbst.";

interface TakeOverProps {
  caseId: string;
  documentJson: DemoRunResult["documentJson"];
  onDone: () => void;
}

/**
 * The one step that turns a reading into Stand 1 — a person's click, never
 * the worker (A-014, B2). A Vorgang that already has a Stand answers 409, and
 * the sentence says what is possible instead.
 */
export function TakeOver({ caseId, documentJson, onDone }: TakeOverProps) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  if (documentJson == null) {
    return (
      <StateMessage
        title="Nichts zu übernehmen"
        // For this Vorgang, not a new one beside it (UX gate round 1, R-4).
        action={<Link className="inline-link" to={`/umriss?case=${encodeURIComponent(caseId)}`}>Umriss selbst zeichnen</Link>}
      >
        {NOTHING_TO_TAKE_OVER}
      </StateMessage>
    );
  }

  async function takeOver() {
    setBusy(true);
    setProblem(null);
    try {
      await postVersion(caseId, {
        expectedVersionNo: 0,
        ops: [],
        document: documentJson,
        planningState: EMPTY_STATE,
        changeSummary: "Stand 1 aus der Auslese",
      });
      await queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      onDone();
    } catch (cause) {
      // `StaleVersionError` carries its own German message already
      // (`api/cases.ts`); every other failure needs `.userMessage` (PF-5) —
      // `ProblemError.message` is the problem's title, not the server's
      // sentence.
      setProblem(
        cause instanceof StaleVersionError
          ? ALREADY_HAS_OUTLINE
          : cause instanceof ProblemError
            ? cause.userMessage
            : cause instanceof Error
              ? cause.message
              : String(cause),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.takeOver}>
      <p>
        Die Form ist ein Vorschlag. Mit „Übernehmen“ wird sie Stand 1 dieses Vorgangs — danach
        planst du die Roste.
      </p>
      <Button onClick={() => void takeOver()} disabled={busy}>
        {busy ? "Wird übernommen …" : "Übernehmen"}
      </Button>
      {problem ? (
        <StateMessage tone="conflict" title="Nicht übernommen.">
          {problem}
        </StateMessage>
      ) : null}
    </div>
  );
}
